// src/pages/PatientHealthTimeline.jsx
// Route: /patient/timeline
//
// "My Health Timeline" — IP Strategy Layer 5, Action #12 Sprint 10 deliverable.
//
// IP Strategy: "The emotional moat — patients experience their health story,
// not a database. Strong retention driver and the primary reason the data moat
// compounds. Patients cannot take this history elsewhere without losing years
// of trajectory context."
//
// Sections:
//   1. Health records — consensus risk score per draw (from triage reports),
//      a horizontally scrollable rail (oldest → newest, left → right,
//      scrolled to the newest by default) with left/right arrow nav — same
//      scroll-rail pattern PatientTodayView's health-score chart uses
//      (.scroll-rail/.scroll-nav in index.css), so scrolling left is how you
//      reach older draws instead of a "Show earlier" click growing a capped
//      list.
//
// The Previu Age / biological-age trajectory is intentionally not shown here —
// it is not yet cleared for the patient/subscriber-facing timeline. Marker
// trends (per-marker sparklines) and the current-markers summary both moved
// to their own "Markers" tab (see MyHealthTabs, PatientCurrentMarkersPage) —
// clicking a marker there shows its trend instead of this page listing the
// top few movers on its own.
//
// Data sources:
//   GET /api/previu-age/{patientId}/history       — used only to detect whether any
//                                                    history exists at all (empty-state check)
//   GET /api/patients/{patientId}/triage-history  — draw-level triage results (patient JWT)
//
// Sprint 13 changes:
//   - RiskTimeline rows now link to /patient/results/:triageId (Sprint 13 item #2)
//   - useNavigate added for programmatic navigation
//   - "View full report →" CTA on each risk row

import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import EmptyState from '../components/EmptyState';
import PageHead from '../components/PageHead';
import MyHealthTabs from '../components/MyHealthTabs';
import CardLink from '../components/CardLink';
import Icon from '../components/Icon';
import { healthJourneyAPI, healthRecordAPI } from '../services/api';
import HeldReportNotice, { isHeld } from '../components/HeldReportNotice';
import { HealthScoreHero } from './PatientTodayView';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const token = () =>
  localStorage.getItem('previu_token') || localStorage.getItem('token');

const fetchJSON = async (url) => {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

// ── Risk level helpers ───────────────────────────────────────────────────────

function riskColor(level) {
  if (!level) return 'var(--text-muted)';
  const l = level.toLowerCase();
  if (l === 'low')      return 'var(--green)';
  if (l === 'moderate') return 'var(--amber)';
  if (l === 'high')     return 'var(--red)';
  return 'var(--text-muted)';
}

function riskBg(level) {
  if (!level) return 'var(--bg-subtle)';
  const l = level.toLowerCase();
  if (l === 'low')      return 'var(--green-bg)';
  if (l === 'moderate') return 'var(--amber-bg)';
  if (l === 'high')     return 'var(--red-bg)';
  return 'var(--bg-subtle)';
}

function riskLabel(level) {
  if (!level) return '—';
  const l = level.toLowerCase();
  if (l === 'low')      return 'Low risk';
  if (l === 'moderate') return 'Moderate';
  if (l === 'high')     return 'High risk';
  return level;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

// ── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, subtitle, action, children }) {
  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px 24px',
      marginBottom: 20,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 12, marginBottom: 16,
      }}>
        <div>
          <div style={{
            fontSize: 15, fontWeight: 600,
            color: 'var(--text-primary)',
          }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── Risk score timeline section ──────────────────────────────────────────────
// Sprint 13: each row now has a "View full report →" link to /patient/results/:triageId
//
// A horizontal scroll rail, not a vertical list capped by a "Show earlier"
// click — every draw is in the DOM (rarely more than a couple dozen even
// over years of blood draws), oldest to newest left to right, scrolled to
// the newest (rightmost) by default. Scrolling left is how you reach older
// draws — the left/right arrow buttons are the click alternative to
// dragging, same .scroll-rail/.scroll-nav pattern PatientTodayView's
// health-score chart already uses (see index.css).

function RiskTimeline({ reports, onViewReport }) {
  const railRef = useRef(null);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  // Default view is the newest draw — scroll all the way right on mount and
  // whenever the report list changes (e.g. a new upload lands).
  useLayoutEffect(() => {
    const el = railRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
    updateScrollButtons();
  }, [reports, updateScrollButtons]);

  if (!reports?.length) return (
    <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
      No triage reports found.
    </div>
  );

  // Ascending by date, tie-broken by createdAt — same two-key ordering the
  // backend's own findLatestPerHealthRecordWithDrawDate query documents
  // (COALESCE(drawDate,...) DESC, createdAt DESC — a re-run beats the
  // original it replaced). Sorting by date alone left same-day draws in
  // whatever order they happened to arrive in `reports`, so a re-run and
  // the report it replaced (same drawDate, different createdAt) could sort
  // either way here — the wrong one could land last and get the "Latest"
  // badge, disagreeing with the Today screen's milestone card, which reads
  // straight from that same backend ordering via HealthJourneyService.
  const sorted = [...reports].sort((a, b) => {
    const dateDiff = new Date(a.drawDate || a.createdAt) - new Date(b.drawDate || b.createdAt);
    return dateDiff !== 0 ? dateDiff : new Date(a.createdAt) - new Date(b.createdAt);
  });

  function scrollByPage(direction) {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={railRef}
        onScroll={updateScrollButtons}
        className="scroll-rail"
        style={{
          display: 'flex', gap: 8, overflowX: 'auto',
          WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth',
          padding: '2px 4px',
        }}
      >
        {sorted.map((r, i) => {
          const isLatest = i === sorted.length - 1;
          const drawDate = formatDate(r.drawDate || r.createdAt);
          const rLevel   = r.consensusRiskLevel;
          const rScore   = r.consensusRiskScore;
          const previuSt = (() => {
            try {
              const raw = typeof r.claudeRawResponse === 'string'
                ? JSON.parse(r.claudeRawResponse) : r.claudeRawResponse;
              return raw?.previu_state;
            } catch { return null; }
          })();

          return (
            <div key={r.id} style={{
              // 320px on wide screens, but never wider than the rail itself: on a
              // ~360px phone the rail is ~290px, and a fixed 320px card was clipped
              // on its left edge when the rail scrolled to the newest.
              minWidth: 'min(320px, 100%)', maxWidth: 'min(320px, 100%)', flexShrink: 0,
              padding: '12px 14px',
              background: isLatest ? 'var(--bg-subtle)' : 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${riskColor(rLevel)}`,
              borderRadius: 'var(--radius-md)',
            }}>
              {/* Top row: date + badge + previu state.
                  confidenceLevel (LOW/MODERATE/HIGH — draw-count-based
                  trend confidence, see HealthRecordService.java) is
                  deliberately not shown here: it's a clinical judgement for
                  Doctor/Navigator, not something a patient should see
                  unexplained next to their risk score. DoctorTriageView.jsx
                  and the navigator's HealthRecordsTab.js still show it. */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                {/* Date + Latest label */}
                <div style={{ minWidth: 95 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{drawDate}</div>
                  {isLatest && (
                    <div style={{
                      fontSize: 10, fontWeight: 600, color: 'var(--brand-accent)',
                      textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 2,
                    }}>
                      Latest
                    </div>
                  )}
                </div>

                {/* Risk badge */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 20,
                  background: riskBg(rLevel),
                  fontSize: 12, fontWeight: 600, color: riskColor(rLevel),
                  flexShrink: 0,
                }}>
                  {riskLabel(rLevel)}
                  {rScore != null && (
                    <span style={{ fontWeight: 400, fontSize: 11 }}>{rScore}/100</span>
                  )}
                </div>

                {/* Previu state pill */}
                {previuSt && (
                  <div style={{
                    fontSize: 11, color: 'var(--text-muted)',
                    alignSelf: 'center',
                    padding: '2px 8px', borderRadius: 12,
                    background: 'var(--bg-subtle)',
                    border: '1px solid var(--border)',
                  }}>
                    {previuSt}
                  </div>
                )}
              </div>

              {/* Key finding */}
              {r.claudeKeyFindings && (
                <div style={{
                  fontSize: 12, color: 'var(--text-secondary)',
                  lineHeight: 1.5, marginTop: 8,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {r.claudeKeyFindings}
                </div>
              )}

              {/* Sprint 13: View full report CTA */}
              <div style={{ marginTop: 10 }}>
                <CardLink
                  onClick={() => onViewReport(r.id)}
                  ariaLabel={`View full report for draw on ${drawDate}`}
                >
                  View full report
                </CardLink>
              </div>
            </div>
          );
        })}
      </div>

      {canScrollLeft && (
        <button type="button" aria-label="Scroll to older health records" onClick={() => scrollByPage(-1)}
          className="scroll-nav scroll-nav--left">
          <span aria-hidden="true" style={{ display: 'inline-flex', transform: 'scaleX(-1)' }}>
            <Icon name="chevronRight" size={14} />
          </span>
        </button>
      )}
      {canScrollRight && (
        <button type="button" aria-label="Scroll to newer health records" onClick={() => scrollByPage(1)}
          className="scroll-nav scroll-nav--right">
          <Icon name="chevronRight" size={14} />
        </button>
      )}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────

/**
 * The empty timeline asked the patient to upload a report and gave them no way
 * to do it — no button, no link, on the highest-intent screen in the portal.
 *
 * The card was hand-rolled rather than using the shared EmptyState, which is
 * how it came to skip the action: EmptyState has rendered a button from an
 * `action` prop all along (PatientRecordsPage uses it), so the fix is to use
 * the component rather than to add anything.
 *
 * .btn-primary (brand-dark) is what EmptyState renders. Deliberately not the
 * gold of the topbar's Upload report: on a white card the primary button is the
 * right weight, and the topbar only uses gold because primary would vanish
 * against the navy bar.
 */
function EmptyTimeline({ onUpload, onOrder }) {
  return (
    <EmptyState
      framed
      icon="trend"
      title="Your health story starts here"
      message={'Upload your first blood test report to begin building your personal health '
        + 'timeline. Each draw adds a chapter — your risk trends and marker '
        + 'trajectories appear here.'}
      action={{ label: 'Upload your first report', onClick: onUpload }}
      secondaryAction={{ label: 'Order a blood test', onClick: onOrder }}
      hint="Already had a test elsewhere? Any lab's PDF works."
    />
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PatientHealthTimeline() {
  const { patient } = usePatient();
  const navigate    = useNavigate();

  const [ageHistory,   setAgeHistory]   = useState([]);
  const [reports,      setReports]      = useState([]);
  // Health-score timeline graph (same data the Today screen's hero uses).
  // null until loaded; stays null if the fetch fails — the graph is then
  // simply omitted rather than blocking the report cards below it.
  const [journey,      setJourney]      = useState(null);
  // Reports uploaded but not yet released (being read, or checked by a navigator). They have no triage yet, so they would
  // otherwise be missing from this page entirely and the patient would think the upload was lost.
  const [heldReports,  setHeldReports]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const load = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    setError(null);

    try {
      const [ageRes, rptRes, journeyRes, recordsRes] = await Promise.allSettled([
        fetchJSON(`${API_BASE}/previu-age/${patient.id}/history`),
        fetchJSON(`${API_BASE}/patients/${patient.id}/triage-history`),
        healthJourneyAPI.get(patient.id),
        healthRecordAPI.list(patient.id),
      ]);

      if (recordsRes.status === 'fulfilled') {
        const data = Array.isArray(recordsRes.value?.data) ? recordsRes.value.data : (recordsRes.value?.data?.content || []);
        setHeldReports(data.filter(isHeld));
      }

      if (journeyRes.status === 'fulfilled') setJourney(journeyRes.value?.data ?? null);

      if (ageRes.status === 'fulfilled' && ageRes.value) {
        // Deduplicate history by latestDrawDate — keep one row per distinct draw date
        // (the most recently created, i.e. last in the array which is ordered asc).
        // This prevents duplicate rows when backfill is called multiple times for the
        // same patient (each call appends a new DB row but uses the same draw data).
        const rawAge = Array.isArray(ageRes.value) ? ageRes.value : [];
        const seenDates = new Map();
        for (const row of rawAge) {
          const key = row.latestDrawDate ?? row.createdAt ?? row.id;
          seenDates.set(key, row); // last-write wins → most recently created row per date
        }
        setAgeHistory(Array.from(seenDates.values()));
      }

      if (rptRes.status === 'fulfilled' && rptRes.value) {
        const data = Array.isArray(rptRes.value) ? rptRes.value : rptRes.value?.content || [];
        // Deduplicate by healthRecordId — keep only the latest triage per health record.
        // Re-running triage on the same health record creates multiple triage_results rows.
        const latestByRecord = {};
        data.forEach(r => {
          const key = r.healthRecordId || r.drawDate || r.id;
          const existing = latestByRecord[key];
          if (!existing || new Date(r.createdAt) > new Date(existing.createdAt)) {
            latestByRecord[key] = r;
          }
        });
        setReports(Object.values(latestByRecord));
      }
    } catch {
      setError('Could not load your health timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  useEffect(() => { load(); }, [load]);

  // Sprint 13: navigate to results page for a specific triage report
  const handleViewReport = useCallback((triageId) => {
    navigate(`/patient/results/${triageId}`);
  }, [navigate]);

  const hasData = ageHistory.length > 0 || reports.length > 0;
  const hasScoreGraph = !!journey && (journey.milestones || []).some(m => m.score != null);

  // breadcrumbs: every other patient page passes one, so this was the only
  // page whose topbar went blank.
  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Health', to: '/patient/timeline' }, { label: 'Health Story' }]}>
      <div className="page-shell page-shell--health">
        <MyHealthTabs />

        {/* The title was a plain <div>, so this page had no h1 at all — a
            screen reader announced no heading for the patient's own record.
            The eyebrow was grey where My Care Plan's is gold, and the title
            was 24px in the body font where My Care Plan's is the display
            face at the same size. PageHead settles all three. */}
        <PageHead
          eyebrow="My health timeline"
          title="Your health story"
          subtitle="Every blood draw adds to your longitudinal health record. Your
                    risk trends and marker trajectories compound in value with each visit."
        />

        {loading && (
          <div style={{
            textAlign: 'center', padding: '40px 0',
            color: 'var(--text-muted)', fontSize: 14,
          }}>
            Loading your health timeline…
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-error" role="alert">{error}</div>
        )}

        {!loading && !hasData && heldReports.length === 0 && (
          <EmptyTimeline
            onUpload={() => navigate('/patient/upload')}
            onOrder={() => navigate('/patient/lab-tests/order')}
          />
        )}

        {!loading && heldReports.length > 0 && (
          <Section title="Reports being checked" subtitle="These will join your timeline once the check is done">
            {heldReports.map(r => (
              <div key={r.id} className="card" style={{ marginBottom: 12 }}>
                <strong>{r.fileName || 'Your report'}</strong>
                <HeldReportNotice record={r} />
              </div>
            ))}
          </Section>
        )}

        {/* The health-score timeline graph — baseline, started care, latest
            report and the planned follow-up. Same card as the Today screen's
            Metric-oriented hero, so Simplified-view patients (whose Today
            screen has no graph) still have it here. */}
        {!loading && hasScoreGraph && (
          <div style={{ marginBottom: 24 }}>
            <HealthScoreHero patient={patient} journey={journey} showGreeting={false} />
          </div>
        )}

        {/* Health records — every consensus risk score, oldest to newest in
            a horizontally scrollable rail (RiskTimeline), scrolled to the
            newest by default; scroll (or the left/right arrows) to reach
            older draws instead of a "Show earlier" click growing a capped
            list. The "Your current markers" summary and "Marker trends"
            that used to live here both moved to their own Markers tab
            (MyHealthTabs) — current markers because it's a full page, not a
            summary card; marker trends because clicking a marker there now
            shows that marker's own trend instead of this page picking a
            handful of top movers on its own. */}
        {!loading && hasData && (
          <Section
            title="Health Records"
            subtitle="Consensus score from Claude"
          >
            <RiskTimeline
              reports={reports}
              onViewReport={handleViewReport}
            />
          </Section>
        )}

      </div>
    </PatientAppShell>
  );
}
